import { computed, inject, Injectable, signal } from '@angular/core';
import { Application, Industry, MatchmakingContent, MatchmakingData, MatchmakingFormConfig, VariantOption } from 'matchmaking/models';
import { MatchmakingTranslateService } from './matchmaking-translate.service';

@Injectable({
    providedIn: 'root'
})
export class MatchmakingDataService {
    private translationService = inject(MatchmakingTranslateService);

    loading = signal(false);
    error = signal<string | null>(null);
    config = signal<MatchmakingFormConfig | null>(null);
    content = signal<MatchmakingContent | null>(null);
    formHasSubmitted = signal<boolean>(false);
    applicationAsProperty = computed(() => this.selectedApplicationTitle().replace(/ /g, '_').toLowerCase() ?? '');

    selectedIndustryTitle = computed(() => {
        return this.getSelectedIndustry()?.title ?? '';
    });

    selectedApplicationTitle = computed(() => {
        return this.getSelectedApplication()?.title ?? '';
    });


    setData(data: MatchmakingData) {
        this.content.set(data.content);
        this.config.set(data.config);
        this.translationService.translations.set(data.translations);
    }

    setConfig(config: MatchmakingFormConfig) {
        this.config.set(config);
    }

    updateConfig(updates: Partial<MatchmakingFormConfig>) {
        if (this.config() == null) return;

        this.config.update((current) => ({
            ...current!,
            ...updates
        }));
    }

    setFormHasSubmitted() {
        this.formHasSubmitted.set(true);
    }

    getIndustryById(title: string): number {
        const industry = this.content()?.industries.find(i => i.title === title);
        return industry?.externalId || Industry.Unknown;
    }

    getApplicationById(title: string): number {
        const industries = this.content()?.industries;

        if (industries) {
            for (const industry of industries) {
                const matchedApplication = industry.variantOptions.find(
                    (app) => app.title === title
                );

                if (matchedApplication) {
                    return matchedApplication.externalId;
                }
            }
        }

        return Application.Unknown;
    }

    getSelectedApplication(): VariantOption | null {
        const selectedApplication = this.config()?.selectedApplication;
        const industries = this.content()?.industries;

        if (!selectedApplication || !industries) {
            return null;
        }

        for (const industry of industries) {
            const matchedApplication = industry.variantOptions.find(
                (app) => app.externalId === selectedApplication
            );

            if (matchedApplication) {
                return matchedApplication;
            }
        }

        return null;
    }

    getSelectedIndustry(): VariantOption | null {
        const industries = this.content()?.industries;

        if (!industries) {
            return null;
        }

        const mappedIndustry = industries.find(
            (industry) => industry.externalId === this.config()?.selectedIndustry
        );

        if (mappedIndustry) {
            return mappedIndustry;
        }

        return null;
    }
}

